import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  Video, Square, Play, RotateCcw, Upload, Check, X, 
  Mic, MicOff, Camera, CameraOff, Clock, AlertCircle,
  Share2, Download, Copy, Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function RecordingStudio() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  
  // Recording states
  const [isRecording, setIsRecording] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const [duration, setDuration] = useState(0);
  const [maxDuration] = useState(user?.subscription_tier === 'business' ? 120 : user?.subscription_tier === 'pro' ? 60 : 30);
  
  // Media states
  const [stream, setStream] = useState(null);
  const [hasPermission, setHasPermission] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);
  
  // Upload states
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedVideo, setUploadedVideo] = useState(null);
  const [error, setError] = useState(null);
  const [editingRequested, setEditingRequested] = useState(false);
  const [requestingEditing, setRequestingEditing] = useState(false);
  
  // Form states
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [showForm, setShowForm] = useState(false);
  
  // Consent state
  const [hasConsented, setHasConsented] = useState(false);
  const [showConsentModal, setShowConsentModal] = useState(true);
  
  // Refs
  const videoRef = useRef(null);
  const previewRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  // Initialize camera
  const initCamera = useCallback(async () => {
    try {
      // Check if getUserMedia is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setHasPermission(false);
        setError('Your browser does not support video recording. Please try Chrome, Firefox, or Safari.');
        return;
      }

      // Try with ideal constraints first, fallback to basic if fails
      let mediaStream;
      try {
        // Desktop-friendly constraints
        mediaStream = await navigator.mediaDevices.getUserMedia({
          video: { 
            width: { ideal: 1280, min: 640 }, 
            height: { ideal: 720, min: 480 }, 
            facingMode: 'user' 
          },
          audio: true
        });
      } catch (constraintError) {
        console.log('Trying with basic constraints...');
        // Fallback to basic constraints for mobile/older devices
        mediaStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user' },
          audio: true
        });
      }
      
      setStream(mediaStream);
      setHasPermission(true);
      setError(null);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (err) {
      console.error('Camera access error:', err);
      setHasPermission(false);
      
      // Provide specific error messages
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setError('Camera permission denied. Please allow camera access in your browser settings and refresh the page.');
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        setError('No camera found. Please connect a camera and try again.');
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        setError('Camera is being used by another app. Please close other apps using the camera and try again.');
      } else if (err.name === 'OverconstrainedError') {
        setError('Camera does not support the required settings. Trying with basic settings...');
        // Try one more time with absolute minimum constraints
        try {
          const basicStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
          setStream(basicStream);
          setHasPermission(true);
          setError(null);
          if (videoRef.current) {
            videoRef.current.srcObject = basicStream;
          }
          return;
        } catch (e) {
          setError('Could not access camera. Please check your device settings.');
        }
      } else {
        setError(`Camera error: ${err.message || 'Unknown error'}. Please check your browser permissions.`);
      }
    }
  }, []);

  useEffect(() => {
    if (loading) return; // Wait for auth check to complete
    if (!user) {
      navigate('/login', { state: { from: '/studio' } });
      return;
    }
    initCamera();
    
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [user, loading, navigate, initCamera]);

  // Ensure video element has stream attached (handles React re-renders)
  useEffect(() => {
    if (stream && videoRef.current && !isPreviewing) {
      videoRef.current.srcObject = stream;
      videoRef.current.play().catch(err => {
        console.log('Video autoplay prevented:', err);
      });
    }
  }, [stream, isPreviewing]);

  // Start recording
  const startRecording = () => {
    if (!stream) return;
    
    chunksRef.current = [];
    setDuration(0);
    setError(null);
    
    // Determine best supported mimeType for cross-browser compatibility
    let mimeType = 'video/webm;codecs=vp9,opus';
    if (!MediaRecorder.isTypeSupported(mimeType)) {
      mimeType = 'video/webm;codecs=vp8,opus';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'video/webm';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = 'video/mp4'; // Safari/iOS fallback
        }
      }
    }
    
    const mediaRecorder = new MediaRecorder(stream, { mimeType });
    
    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        chunksRef.current.push(e.data);
      }
    };
    
    mediaRecorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: 'video/webm' });
      setRecordedBlob(blob);
      setRecordedUrl(URL.createObjectURL(blob));
      setIsPreviewing(true);
    };
    
    mediaRecorderRef.current = mediaRecorder;
    mediaRecorder.start(1000);
    setIsRecording(true);
    
    // Start timer
    timerRef.current = setInterval(() => {
      setDuration(prev => {
        if (prev >= maxDuration - 1) {
          stopRecording();
          return prev;
        }
        return prev + 1;
      });
    }, 1000);
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };

  // Retake
  const handleRetake = () => {
    setRecordedBlob(null);
    setRecordedUrl(null);
    setIsPreviewing(false);
    setDuration(0);
    setShowForm(false);
    setTitle('');
    setDescription('');
  };

  // Toggle mute
  const toggleMute = () => {
    if (stream) {
      stream.getAudioTracks().forEach(track => {
        track.enabled = !track.enabled;
      });
      setIsMuted(!isMuted);
    }
  };

  // Toggle camera
  const toggleCamera = () => {
    if (stream) {
      stream.getVideoTracks().forEach(track => {
        track.enabled = !track.enabled;
      });
      setIsCameraOff(!isCameraOff);
    }
  };

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Upload video
  const handleUpload = async () => {
    if (!recordedBlob || !title.trim()) {
      setError('Please enter a title for your video');
      return;
    }
    
    setIsUploading(true);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('video', recordedBlob, 'recording.webm');
      formData.append('title', title);
      formData.append('description', description);
      formData.append('duration', duration);
      
      const response = await axios.post(`${API_URL}/api/videos/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      
      setUploadedVideo(response.data);
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.detail || 'Failed to upload video. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  // Copy share link
  const copyShareLink = () => {
    if (uploadedVideo) {
      const shareUrl = `${window.location.origin}/v/${uploadedVideo.share_id}`;
      navigator.clipboard.writeText(shareUrl);
      alert('Link copied to clipboard!');
    }
  };

  // Request professional editing
  const requestEditing = async () => {
    if (!uploadedVideo) return;
    setRequestingEditing(true);
    try {
      await axios.post(`${API_URL}/api/videos/${uploadedVideo.id}/request-editing`, {}, { withCredentials: true });
      setEditingRequested(true);
    } catch (err) {
      console.error('Editing request error:', err);
      setError(err.response?.data?.detail || 'Failed to send editing request.');
    } finally {
      setRequestingEditing(false);
    }
  };

  // Show loading while checking auth
  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 pt-24 pb-16 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
      </div>
    );
  }

  // If not logged in
  if (!user) {
    return null;
  }

  // Success screen
  if (uploadedVideo) {
    const shareUrl = `${window.location.origin}/v/${uploadedVideo.share_id}`;
    
    return (
      <div className="min-h-screen bg-zinc-950 pt-24 pb-16" data-testid="upload-success">
        <div className="max-w-2xl mx-auto px-6">
          {/* Upload Status Steps */}
          <div className="mb-8">
            <div className="flex items-center justify-center gap-0 mb-6">
              {[
                { label: 'Recorded', icon: '1' },
                { label: 'Saved', icon: '2' },
                { label: 'Uploaded to BrandStage', icon: '3' },
              ].map((step, i) => (
                <div key={i} className="flex items-center">
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center mb-1">
                      <Check className="w-4 h-4 text-green-500" />
                    </div>
                    <span className="text-xs text-green-400 font-medium whitespace-nowrap">{step.label}</span>
                  </div>
                  {i < 2 && <div className="w-10 sm:w-16 h-0.5 bg-green-500/30 mx-1 mb-5" />}
                </div>
              ))}
            </div>
            <div className="text-center">
              <h1 className="text-2xl sm:text-3xl font-black mb-2" data-testid="upload-success-title">Video Uploaded Successfully!</h1>
              <p className="text-zinc-400 text-sm">Your video is securely stored on BrandStage. The admin team has been notified and will review it shortly.</p>
            </div>
          </div>
          
          <div className="bg-zinc-900 border border-zinc-800 p-6 mb-6">
            <video 
              src={`${API_URL}${uploadedVideo.video_url}`}
              controls
              className="w-full aspect-video bg-black mb-4"
              data-testid="uploaded-video-preview"
            />
            <h2 className="text-xl font-bold mb-2">{uploadedVideo.title}</h2>
            {uploadedVideo.description && (
              <p className="text-zinc-400 text-sm mb-4">{uploadedVideo.description}</p>
            )}
            
            {/* Professional Editing CTA */}
            {!editingRequested ? (
              <div className="bg-gradient-to-r from-[#C41E3A]/10 to-zinc-900 border border-[#C41E3A]/30 rounded-lg p-5 mb-4" data-testid="editing-prompt">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#C41E3A]/20 flex items-center justify-center shrink-0 mt-0.5">
                    <Video className="w-5 h-5 text-[#C41E3A]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-white mb-1">Want professional editing?</h3>
                    <p className="text-zinc-400 text-sm mb-3">
                      Our team can add BrandStage branding, polish your video, and make it presentation-ready. Timelines and scope will be discussed with you directly.
                    </p>
                    <button
                      onClick={requestEditing}
                      disabled={requestingEditing}
                      className="bg-[#C41E3A] hover:bg-[#A01830] text-white px-5 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
                      data-testid="request-editing-btn"
                    >
                      {requestingEditing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      Yes, send to BrandStage team
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-4 flex items-center gap-3" data-testid="editing-requested">
                <Check className="w-5 h-5 text-green-400 shrink-0" />
                <div>
                  <p className="text-green-400 font-semibold text-sm">Editing request sent!</p>
                  <p className="text-zinc-400 text-xs">The BrandStage team will reach out to discuss timelines and details.</p>
                </div>
              </div>
            )}
            
            <div className="bg-zinc-950 border border-zinc-800 p-4 rounded-sm mb-4">
              <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Share Link</p>
              <div className="flex items-center gap-2">
                <input 
                  type="text" 
                  value={shareUrl}
                  readOnly
                  className="flex-1 bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm rounded-sm"
                  data-testid="share-link-input"
                />
                <button 
                  onClick={copyShareLink}
                  className="p-2 bg-zinc-800 hover:bg-zinc-700 transition-colors rounded-sm"
                  data-testid="copy-link-btn"
                >
                  <Copy className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="flex gap-4">
              <a 
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 btn-primary rounded-sm text-center"
                data-testid="share-linkedin"
              >
                Share on LinkedIn
              </a>
              <a 
                href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(uploadedVideo.title)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 btn-secondary rounded-sm text-center"
                data-testid="share-twitter"
              >
                Share on Twitter
              </a>
            </div>
          </div>
          
          <div className="flex gap-4">
            <button 
              onClick={handleRetake}
              className="flex-1 btn-secondary rounded-sm"
              data-testid="record-another-btn"
            >
              Record Another
            </button>
            <button 
              onClick={() => navigate('/dashboard')}
              className="flex-1 btn-primary rounded-sm"
              data-testid="go-dashboard-btn"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 pt-24 pb-16" data-testid="recording-studio">
      <div className="max-w-4xl mx-auto px-6">
        {/* Recording Consent Modal */}
        {showConsentModal && !hasConsented && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-zinc-900 border border-zinc-700 rounded-xl max-w-lg w-full p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-[#C41E3A]/20 rounded-full flex items-center justify-center">
                  <Video className="w-6 h-6 text-[#C41E3A]" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Recording Consent</h2>
                  <p className="text-zinc-400 text-sm">Please review before recording</p>
                </div>
              </div>
              
              <div className="bg-zinc-800/50 rounded-lg p-4 mb-4 text-sm text-zinc-300 space-y-3">
                <p>By proceeding with video recording, you acknowledge and consent to the following:</p>
                <ul className="space-y-2 ml-4">
                  <li className="flex items-start gap-2">
                    <span className="text-[#C41E3A] mt-1">•</span>
                    <span>Your video will be <strong>recorded and processed</strong> by BrandStage</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#C41E3A] mt-1">•</span>
                    <span>Videos are stored on <strong>cloud servers (Cloudinary)</strong> and may include BrandStage branding</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#C41E3A] mt-1">•</span>
                    <span>You <strong>retain ownership</strong> of your content and can delete it anytime</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#C41E3A] mt-1">•</span>
                    <span>Published videos may be <strong>publicly accessible</strong> via share links</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#C41E3A] mt-1">•</span>
                    <span>Raw recordings and retakes are <strong>automatically deleted</strong> after processing</span>
                  </li>
                </ul>
              </div>
              
              <div className="flex items-start gap-3 mb-6">
                <input
                  type="checkbox"
                  id="recording-consent"
                  checked={hasConsented}
                  onChange={(e) => setHasConsented(e.target.checked)}
                  className="mt-1 w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-[#C41E3A] focus:ring-[#C41E3A]"
                  data-testid="recording-consent-checkbox"
                />
                <label htmlFor="recording-consent" className="text-sm text-zinc-300">
                  I have read and agree to the <a href="/terms" target="_blank" className="text-[#C41E3A] hover:underline">Terms of Service</a> and <a href="/privacy" target="_blank" className="text-[#C41E3A] hover:underline">Privacy Policy</a>, and I consent to being recorded.
                </label>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => navigate('/dashboard')}
                  className="flex-1 px-4 py-3 bg-zinc-800 hover:bg-zinc-700 rounded-lg font-medium transition-colors"
                  data-testid="consent-cancel-btn"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (hasConsented) {
                      axios.post(`${API_URL}/api/consents`, { kind: 'recording_studio', details: { page: 'studio' } }, { withCredentials: true }).catch(() => {});
                      setShowConsentModal(false);
                    }
                  }}
                  disabled={!hasConsented}
                  className={`flex-1 px-4 py-3 rounded-lg font-medium transition-colors ${
                    hasConsented 
                      ? 'bg-[#C41E3A] hover:bg-[#A01830] text-white' 
                      : 'bg-zinc-700 text-zinc-500 cursor-not-allowed'
                  }`}
                  data-testid="consent-continue-btn"
                >
                  Continue to Record
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-black tracking-tight mb-2">Recording Studio</h1>
          <p className="text-zinc-400">
            Record your {maxDuration}-second quick bite video. 
            {user.videos_count >= user.videos_limit && (
              <span className="text-[#C41E3A]"> You've reached your monthly limit. Upgrade to record more.</span>
            )}
          </p>
        </div>

        {/* Video Container */}
        <div className="relative bg-black aspect-video mb-6 border border-zinc-800" data-testid="video-container">
          {/* Permission denied */}
          {hasPermission === false && (
            <div className="absolute inset-0 flex items-center justify-center p-6">
              <div className="text-center max-w-md">
                <AlertCircle className="w-12 h-12 text-[#C41E3A] mx-auto mb-4" />
                <p className="text-white font-bold mb-2">Camera Access Required</p>
                {error && (
                  <p className="text-zinc-400 text-sm mb-4">{error}</p>
                )}
                <div className="bg-zinc-900 border border-zinc-700 p-4 rounded-sm mb-4 text-left text-sm">
                  <p className="text-zinc-300 font-medium mb-2">How to enable camera:</p>
                  <ul className="text-zinc-400 space-y-1">
                    <li>• Click the 🔒 lock icon in the address bar</li>
                    <li>• Set Camera and Microphone to "Allow"</li>
                    <li>• Refresh this page</li>
                  </ul>
                  <p className="text-zinc-500 mt-3 text-xs">On mobile: Check your browser's site settings</p>
                </div>
                <button 
                  onClick={initCamera}
                  className="btn-primary rounded-sm"
                  data-testid="retry-camera-btn"
                >
                  Try Again
                </button>
              </div>
            </div>
          )}

          {/* Loading camera */}
          {hasPermission === null && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <Loader2 className="w-12 h-12 text-zinc-500 mx-auto mb-4 animate-spin" />
                <p className="text-zinc-400">Requesting camera access...</p>
                <p className="text-zinc-500 text-sm mt-2">Please allow when prompted</p>
              </div>
            </div>
          )}

          {/* Live preview */}
          {!isPreviewing && hasPermission && (
            <video 
              ref={videoRef}
              autoPlay
              muted
              playsInline
              className={`w-full h-full object-cover ${isCameraOff ? 'hidden' : ''}`}
              data-testid="live-preview"
            />
          )}

          {/* Camera off placeholder */}
          {!isPreviewing && isCameraOff && (
            <div className="absolute inset-0 flex items-center justify-center bg-zinc-900">
              <CameraOff className="w-16 h-16 text-zinc-600" />
            </div>
          )}

          {/* Recorded preview */}
          {isPreviewing && recordedUrl && (
            <video 
              ref={previewRef}
              src={recordedUrl}
              controls
              className="w-full h-full object-cover"
              data-testid="recorded-preview"
            />
          )}

          {/* Recording indicator */}
          {isRecording && (
            <div className="absolute top-4 left-4 flex items-center gap-2 bg-black/50 px-3 py-2 rounded-sm" data-testid="recording-indicator">
              <div className="w-3 h-3 bg-[#C41E3A] rounded-full recording-pulse" />
              <span className="font-mono text-sm text-white">REC</span>
            </div>
          )}

          {/* Timer */}
          <div className="absolute top-4 right-4 bg-black/50 px-3 py-2 rounded-sm" data-testid="timer-display">
            <span className="font-mono text-lg text-white">
              {formatTime(duration)} / {formatTime(maxDuration)}
            </span>
          </div>

          {/* Progress bar */}
          {(isRecording || isPreviewing) && (
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-zinc-800">
              <div 
                className="h-full bg-[#C41E3A] transition-all duration-1000"
                style={{ width: `${(duration / maxDuration) * 100}%` }}
                data-testid="progress-bar"
              />
            </div>
          )}

          {/* Watermark preview (for free tier) */}
          {user.subscription_tier === 'free' && (
            <div className="absolute bottom-4 right-4 text-white/50 text-xs font-bold" data-testid="watermark">
              BrandStage
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="flex items-center justify-center gap-4 mb-8">
          {!isPreviewing ? (
            <>
              {/* Mute toggle */}
              <button 
                onClick={toggleMute}
                className={`p-3 rounded-full border ${isMuted ? 'border-[#C41E3A] text-[#C41E3A]' : 'border-zinc-700 text-zinc-400'} hover:bg-zinc-800 transition-colors`}
                data-testid="mute-toggle"
              >
                {isMuted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
              </button>

              {/* Record button */}
              {!isRecording ? (
                <button 
                  onClick={startRecording}
                  disabled={!hasPermission || user.videos_count >= user.videos_limit}
                  className="w-16 h-16 bg-[#C41E3A] rounded-full flex items-center justify-center hover:bg-[#A01830] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="start-record-btn"
                >
                  <Video className="w-6 h-6 text-white" />
                </button>
              ) : (
                <button 
                  onClick={stopRecording}
                  className="w-16 h-16 bg-[#C41E3A] rounded-full flex items-center justify-center hover:bg-[#A01830] transition-colors"
                  data-testid="stop-record-btn"
                >
                  <Square className="w-6 h-6 text-white fill-white" />
                </button>
              )}

              {/* Camera toggle */}
              <button 
                onClick={toggleCamera}
                className={`p-3 rounded-full border ${isCameraOff ? 'border-[#C41E3A] text-[#C41E3A]' : 'border-zinc-700 text-zinc-400'} hover:bg-zinc-800 transition-colors`}
                data-testid="camera-toggle"
              >
                {isCameraOff ? <CameraOff className="w-5 h-5" /> : <Camera className="w-5 h-5" />}
              </button>
            </>
          ) : (
            <>
              {/* Retake */}
              <button 
                onClick={handleRetake}
                className="flex items-center gap-2 px-6 py-3 border border-zinc-700 text-zinc-400 hover:bg-zinc-800 transition-colors rounded-sm"
                data-testid="retake-btn"
              >
                <RotateCcw className="w-5 h-5" />
                Retake
              </button>

              {/* Save */}
              <button 
                onClick={() => setShowForm(true)}
                className="flex items-center gap-2 px-6 py-3 bg-[#C41E3A] text-white hover:bg-[#A01830] transition-colors rounded-sm"
                data-testid="save-btn"
              >
                <Check className="w-5 h-5" />
                Save & Share
              </button>
            </>
          )}
        </div>

        {/* Error message */}
        {error && (
          <div className="bg-red-900/20 border border-red-800 text-red-400 px-4 py-3 rounded-sm mb-6" data-testid="error-message">
            {error}
          </div>
        )}

        {/* Video details form */}
        {showForm && (
          <div className="bg-zinc-900 border border-zinc-800 p-6 rounded-sm" data-testid="video-form">
            <h3 className="text-lg font-bold mb-4">Video Details</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Title *</label>
                <input 
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Give your video a title"
                  className="input-dark rounded-sm w-full"
                  data-testid="video-title-input"
                />
              </div>
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Description (optional)</label>
                <textarea 
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Add a description..."
                  rows={3}
                  className="input-dark rounded-sm w-full resize-none"
                  data-testid="video-description-input"
                />
              </div>
              <div className="flex gap-4">
                <button 
                  onClick={() => setShowForm(false)}
                  className="flex-1 btn-secondary rounded-sm"
                  data-testid="cancel-form-btn"
                >
                  Cancel
                </button>
                <button 
                  onClick={handleUpload}
                  disabled={isUploading || !title.trim()}
                  className="flex-1 btn-primary rounded-sm flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="upload-video-btn"
                >
                  {isUploading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Saving & Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5" />
                      Save & Upload to BrandStage
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tips */}
        <div className="mt-8 p-6 bg-zinc-900/50 border border-zinc-800 rounded-sm">
          <h3 className="font-bold mb-4 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-[#C41E3A]" />
            Recording Tips
          </h3>
          <ul className="space-y-2 text-sm text-zinc-400">
            <li>• Ensure good lighting - face a window or light source</li>
            <li>• Find a quiet environment with minimal background noise</li>
            <li>• Position your camera at eye level</li>
            <li>• Speak clearly and maintain eye contact with the camera</li>
            <li>• Keep your message concise and impactful</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
